// The Review view's conversation: everything said in review of a pull
// request so far, by every reviewer and the author. Review threads (each
// an open or settled conversation on some lines), submitted reviews with
// their verdicts, and conversation comments. Not the pull request's
// history: commits, pushes, review requests and state changes are the
// Overview's timeline, and are left out here.
//
// Pure: read from the timeline and the review threads the page already
// holds. A thread is listed on its own while it is open (unresolved), so
// it can be answered where it is listed; a settled one is reached through
// the review that wrote it, as in the Overview.
import type { ReviewThread, TimelineItem } from '../api/queries';
import { sameLogin } from './attention';
import { parseOffDiff } from './offdiff';

export type ReviewItem = Extract<TimelineItem, { __typename: 'PullRequestReview' }>;
export type CommentItem = Extract<TimelineItem, { __typename: 'IssueComment' }>;

/** One thing said in review. `at` orders it; `mine` says the viewer wrote it, or wrote in it. */
export type ConversationEntry =
  | { kind: 'thread'; id: string; at: string; mine: boolean; thread: ReviewThread; open: boolean }
  | { kind: 'review'; id: string; at: string; mine: boolean; review: ReviewItem }
  | { kind: 'comment'; id: string; at: string; mine: boolean; comment: CommentItem };

/** Which of it is listed: the open threads, what the viewer took part in, or all of it. */
export type ConversationFilter = 'open' | 'mine' | 'all';

export const FILTERS: readonly { id: ConversationFilter; label: string; title: string }[] = [
  { id: 'open', label: 'Open', title: 'Unresolved threads: the conversations still to answer' },
  { id: 'mine', label: 'Mine', title: 'Your reviews and comments, and the open threads you wrote in' },
  { id: 'all', label: 'All', title: 'Every review, comment and open thread, by every reviewer and the author' },
];

const published = (c: { pullRequestReview?: { state: string } | null }) => c.pullRequestReview?.state !== 'PENDING';

/** When a review was said: submitted, else created. */
export const reviewAt = (r: ReviewItem): string => r.submittedAt ?? r.createdAt;

/** How a submitted review's state reads after its author's name. */
export const REVIEW_WORD: Record<string, string> = {
  APPROVED: 'approved',
  CHANGES_REQUESTED: 'requested changes',
  COMMENTED: 'reviewed',
  DISMISSED: 'review dismissed',
};

/**
 * Whether a submitted review says something of its own: a verdict, a
 * summary, or inline comments. One that only wraps a reply (GitHub makes a
 * review for every reply) with no text is left to its thread.
 */
const speaks = (r: ReviewItem) => r.state !== 'PENDING' && (r.state !== 'COMMENTED' || r.body.trim() !== '' || r.comments.totalCount > 0);

/**
 * Every entry of the review conversation, in the order said: the review
 * threads with a published comment (the viewer's own unsent threads are
 * drafts, listed apart), the submitted reviews and the conversation
 * comments. Nothing else of the timeline is an entry.
 */
export function conversationEntries(timeline: readonly TimelineItem[], threads: readonly ReviewThread[], viewer: string): ConversationEntry[] {
  const by = (login: string | undefined | null) => !!login && !!viewer && sameLogin(login, viewer);
  const out: ConversationEntry[] = [];
  for (const thread of threads) {
    const said = thread.comments.nodes.filter(published);
    if (!said.length) continue;
    out.push({
      kind: 'thread',
      id: thread.id,
      at: said[0].createdAt,
      // A draft reply of the viewer's is theirs too, so a thread they are answering is "mine".
      mine: thread.comments.nodes.some((c) => by(c.author?.login) || !published(c)),
      thread,
      open: !thread.isResolved,
    });
  }
  for (const it of timeline) {
    if (it.__typename === 'PullRequestReview') {
      if (speaks(it)) out.push({ kind: 'review', id: it.id, at: reviewAt(it), mine: by(it.author?.login), review: it });
    } else if (it.__typename === 'IssueComment') {
      out.push({ kind: 'comment', id: it.id, at: it.createdAt, mine: by(it.author?.login), comment: it });
    }
  }
  return out.sort((a, b) => Date.parse(a.at) - Date.parse(b.at) || a.id.localeCompare(b.id));
}

/**
 * The entries `filter` lists, open threads first (the conversations still
 * to answer, longest waiting first), then the reviews and comments in the
 * order said. A resolved thread is not listed on its own: it unfolds
 * inside the review that wrote it.
 */
export function listed(entries: readonly ConversationEntry[], filter: ConversationFilter): ConversationEntry[] {
  const open = entries.filter((e) => e.kind === 'thread' && e.open);
  if (filter === 'open') return open;
  const rest = entries.filter((e) => e.kind !== 'thread');
  const all = [...open, ...rest];
  return filter === 'mine' ? all.filter((e) => e.mine) : all;
}

/** How many entries each filter lists. */
export function filterCounts(entries: readonly ConversationEntry[]): Record<ConversationFilter, number> {
  return { open: listed(entries, 'open').length, mine: listed(entries, 'mine').length, all: listed(entries, 'all').length };
}

/** The filter the conversation opens on: the open threads while there are any, else everything. */
export const startFilter = (entries: readonly ConversationEntry[]): ConversationFilter => (entries.some((e) => e.kind === 'thread' && e.open) ? 'open' : 'all');

/** A thread in one line, for its folded row: who began it, how it reads, and how many replies followed. */
export function threadSummary(thread: ReviewThread): { author: string; text: string; replies: number } {
  const said = thread.comments.nodes.filter(published);
  const first = said[0];
  // Its first line of prose: not the header and hidden marker of a comment for a line outside the diff, a quote or a code block (a suggestion).
  const line =
    (parseOffDiff(first?.body)?.text ?? first?.body ?? '')
      .replace(/<!--[\s\S]*?-->/g, '')
      .replace(/^ {0,3}(`{3,}|~{3,})[\s\S]*?^ {0,3}\1[^\n]*$/gm, '')
      .split('\n')
      .map((l) => l.trim())
      .find((l) => l && !l.startsWith('>')) ?? '';
  return { author: first?.author?.login ?? 'ghost', text: line.length > 140 ? line.slice(0, 139) + '…' : line, replies: Math.max(0, said.length - 1) };
}
